import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ActivityIndicator, Platform, Modal } from 'react-native';
import WebScrollView from '../../components/WebScrollView';
import { useNavigate } from 'react-router-dom';
import { Picker } from '@react-native-picker/picker';
import { 
  ClipboardList, 
  AlertCircle, 
  Clock, 
  Home as HomeIcon, 
  CheckCircle2, 
  Loader2,
  Plus,
  X,
  Calendar,
  User,
  MessageSquare,
  StickyNote,
  Check,
  Trash2,
  MapPin,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Wrench
} from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { AdminTask, ServiceRequest, Profile, Home } from './types';
import { shadows } from '../../lib/styles';
import { formatDateOnly, parseLocalDate } from '../../lib/dateUtils';

interface UpcomingVisit {
  id: string;
  visit_type: 'maintenance' | 'service' | 'walkthrough';
  status: 'requested' | 'proposed' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  scheduled_date: string | null;
  estimated_duration_minutes: number | null;
  internal_name: string | null;
  visit_notes: string | null;
  assigned_to: string | null;
  home: {
    id: string;
    address: string;
    city: string;
    state: string;
    home_manager: string | null;
    owner: { full_name: string | null } | null;
  } | null;
  service_provider: { company_name: string | null } | null;
}

const MyDayTab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<Profile | null>(null);
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [unreadMessagesByHomeowner, setUnreadMessagesByHomeowner] = useState<Record<string, number>>({});
  const [oldestUnreadByHomeowner, setOldestUnreadByHomeowner] = useState<Record<string, string>>({});
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const [allOpenServiceRequests, setAllOpenServiceRequests] = useState<ServiceRequest[]>([]);
  const [homes, setHomes] = useState<Pick<Home, 'id' | 'address' | 'city' | 'state'>[]>([]);
  const [homeowners, setHomeowners] = useState<Pick<Profile, 'id' | 'full_name'>[]>([]);
  const [adminTeamMembers, setAdminTeamMembers] = useState<Pick<Profile, 'id' | 'full_name'>[]>([]);
  const [notepad, setNotepad] = useState('');
  const [notepadSaving, setNotepadSaving] = useState(false);
  const [notepadSaved, setNotepadSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [deletingTask, setDeletingTask] = useState(false);
  const [expandedTaskCards, setExpandedTaskCards] = useState<Set<string>>(new Set());
  const [expandedRequestCards, setExpandedRequestCards] = useState<Set<string>>(new Set());
  const [expandedVisitCards, setExpandedVisitCards] = useState<Set<string>>(new Set());
  const [upcomingVisits, setUpcomingVisits] = useState<UpcomingVisit[]>([]);

  // Widget-level collapse state
  const [messagesExpanded, setMessagesExpanded] = useState(true);
  const [tasksExpanded, setTasksExpanded] = useState(true);
  const [requestsExpanded, setRequestsExpanded] = useState(true);
  const [visitsExpanded, setVisitsExpanded] = useState(true);
  const homeSearchRef = useRef<View>(null);
  const taskHomeSearchRef = useRef<View>(null);

  // Quick create form state
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const getTodayDate = () => formatDateOnly(new Date());
  const [quickCreateForm, setQuickCreateForm] = useState({
    title: '',
    description: '',
    priority: 'normal' as AdminTask['priority'],
    due_date: getTodayDate(),
    home_id: '',
    service_request_id: '',
    assigned_to: ''
  });

  // Task form home search state
  const [taskHomeSearchQuery, setTaskHomeSearchQuery] = useState('');
  const [taskHomeSearchResults, setTaskHomeSearchResults] = useState<Array<{
    id: string;
    address: string;
    city: string;
    state: string;
    owner_name: string;
    display: string;
  }>>([]);
  const [taskHomeSearchLoading, setTaskHomeSearchLoading] = useState(false);
  const [selectedTaskHome, setSelectedTaskHome] = useState<{id: string; display: string} | null>(null);
  const [showTaskHomeSearchResults, setShowTaskHomeSearchResults] = useState(false);

  // Quick create service request form state
  const [quickCreateRequestOpen, setQuickCreateRequestOpen] = useState(false);
  const [creatingRequest, setCreatingRequest] = useState(false);
  const [serviceRequestError, setServiceRequestError] = useState<string | null>(null);
  const [quickCreateRequestForm, setQuickCreateRequestForm] = useState({
    title: '',
    description: '',
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    home_id: ''
  });

  // Home search state
  const [homeSearchQuery, setHomeSearchQuery] = useState('');
  const [homeSearchResults, setHomeSearchResults] = useState<Array<{
    id: string;
    address: string;
    city: string;
    state: string;
    owner_name: string;
    display: string;
  }>>([]);
  const [homeSearchLoading, setHomeSearchLoading] = useState(false);
  const [selectedHome, setSelectedHome] = useState<{id: string; display: string} | null>(null);
  const [showHomeSearchResults, setShowHomeSearchResults] = useState(false);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const saveNotepad = useCallback(async () => {
    if (!currentUser) return;
    
    setNotepadSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ notepad })
        .eq('id', currentUser.id);

      if (error) throw error;
      
      setNotepadSaved(true);
      setTimeout(() => setNotepadSaved(false), 2000);
    } catch (error: any) {
      console.error('Error saving notepad:', error);
    } finally {
      setNotepadSaving(false);
    }
  }, [notepad, currentUser]);

  // Debounced auto-save for notepad
  useEffect(() => {
    if (!currentUser) return;
    
    const timeoutId = setTimeout(() => {
      saveNotepad();
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [notepad, currentUser, saveNotepad]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch current user's profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*, notepad')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      setCurrentUser(profile);
      setNotepad(profile.notepad || '');

      // Fetch tasks assigned to current user
      await fetchTasks(user.id);

      // Fetch only this admin's requests for "My Service Requests"
      await fetchServiceRequests(user.id);
      // Fetch all open requests for optional task association picker
      await fetchAllOpenServiceRequests();

      // Fetch upcoming visits relevant to this manager
      await fetchUpcomingVisits(user.id, profile.full_name);

      // Fetch homes and homeowners for dropdowns
      await fetchHomesAndHomeowners();

      // Set default assigned_to to current user
      setQuickCreateForm(prev => ({ ...prev, assigned_to: user.id }));

    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async (userId: string) => {
    const { data, error } = await supabase
      .from('admin_tasks')
      .select(`
        *,
        home:homes (address, city, state),
        homeowner:profiles!admin_tasks_homeowner_id_fkey (full_name),
        service_request:service_requests (title)
      `)
      .eq('assigned_to', userId)
      .neq('status', 'completed')
      .order('due_date', { ascending: true, nullsFirst: false });

    if (error) throw error;
    setTasks(data || []);
  };

  const fetchServiceRequests = async (userId: string) => {
    // Fetch service requests assigned to this user
    const { data, error } = await supabase
      .from('service_requests')
      .select(`
        *,
        home:homes!inner (
          id,
          address,
          city,
          state,
          owner:profiles!homes_owner_id_fkey (
            id,
            full_name
          )
        )
      `)
      .eq('owner_id', userId)
      .in('status', ['pending', 'in_progress'])
      .order('created_at', { ascending: false });

    if (error) throw error;

    const requests = data || [];
    setServiceRequests(requests);
    await fetchUnreadMessagesForManagedHomes(userId, requests);
  };

  const fetchUnreadMessagesForManagedHomes = async (userId: string, requests: ServiceRequest[]) => {
    const homeownerIds = Array.from(
      new Set(
        requests
          .map((request) => (request.home as any)?.owner?.id || request.owner_id)
          .filter((id): id is string => Boolean(id))
      )
    );

    if (homeownerIds.length === 0) {
      setUnreadMessagesByHomeowner({});
      setOldestUnreadByHomeowner({});
      setTotalUnreadMessages(0);
      return;
    }

    const { data, error } = await supabase
      .from('conversations')
      .select('id, homeowner_id, manager_unread_count')
      .eq('type', 'home_manager')
      .eq('home_manager_id', userId)
      .in('homeowner_id', homeownerIds)
      .gt('manager_unread_count', 0);

    if (error) throw error;

    const conversations = data || [];
    const conversationIds = conversations.map(c => c.id);

    const unreadByHomeowner: Record<string, number> = {};
    const oldestByHomeowner: Record<string, string> = {};
    let unreadTotal = 0;

    conversations.forEach((conversation: { id: string; homeowner_id: string; manager_unread_count: number }) => {
      const currentCount = unreadByHomeowner[conversation.homeowner_id] || 0;
      const nextCount = currentCount + (conversation.manager_unread_count || 0);
      unreadByHomeowner[conversation.homeowner_id] = nextCount;
      unreadTotal += conversation.manager_unread_count || 0;
    });

    if (conversationIds.length > 0) {
      const { data: oldestMessages } = await supabase
        .from('conversation_messages')
        .select('conversation_id, created_at')
        .in('conversation_id', conversationIds)
        .eq('sender_type', 'homeowner')
        .is('read_by_manager_at', null)
        .order('created_at', { ascending: true });

      const convToHomeowner = new Map(conversations.map(c => [c.id, c.homeowner_id]));

      (oldestMessages || []).forEach((msg: { conversation_id: string; created_at: string }) => {
        const homeownerId = convToHomeowner.get(msg.conversation_id);
        if (homeownerId && !oldestByHomeowner[homeownerId]) {
          oldestByHomeowner[homeownerId] = msg.created_at;
        }
      });
    }

    setUnreadMessagesByHomeowner(unreadByHomeowner);
    setOldestUnreadByHomeowner(oldestByHomeowner);
    setTotalUnreadMessages(unreadTotal);
  };

  const fetchAllOpenServiceRequests = async () => {
    // Fetch all active service requests for task association only
    const { data, error } = await supabase
      .from('service_requests')
      .select(`
        *,
        home:homes!inner (
          id,
          address,
          city,
          state,
          owner:profiles!homes_owner_id_fkey (
            id,
            full_name
          )
        )
      `)
      .in('status', ['pending', 'in_progress'])
      .order('created_at', { ascending: false });

    if (error) throw error;
    setAllOpenServiceRequests(data || []);
  };

  const fetchUpcomingVisits = async (userId: string, fullName: string | null | undefined) => {
    // Pull active-pipeline visits where the current user is the home manager
    // (matched by homes.home_manager string) or is directly assigned.
    const trimmedName = (fullName || '').trim();

    const { data, error } = await supabase
      .from('visits')
      .select(`
        id,
        visit_type,
        status,
        scheduled_date,
        estimated_duration_minutes,
        internal_name,
        visit_notes,
        assigned_to,
        home:homes!inner (
          id,
          address,
          city,
          state,
          home_manager,
          owner:profiles!homes_owner_id_fkey (
            full_name
          )
        ),
        service_provider:service_providers!visits_service_provider_id_fkey (
          company_name
        )
      `)
      .in('status', ['requested', 'proposed', 'scheduled', 'in_progress'])
      .order('scheduled_date', { ascending: true, nullsFirst: false });

    if (error) {
      console.error('Error fetching upcoming visits:', error);
      setUpcomingVisits([]);
      return;
    }

    const all = (data || []) as unknown as UpcomingVisit[];
    const filtered = all.filter((visit) => {
      const matchesAssignee = visit.assigned_to === userId;
      const matchesManagerName = trimmedName
        ? (visit.home?.home_manager || '').trim().toLowerCase() === trimmedName.toLowerCase()
        : false;
      return matchesAssignee || matchesManagerName;
    });

    setUpcomingVisits(filtered);
  };

  const fetchHomesAndHomeowners = async () => {
    // Fetch all homes for dropdown
    const { data: homesData, error: homesError } = await supabase
      .from('homes')
      .select('id, address, city, state')
      .order('address');

    if (homesError) throw homesError;
    setHomes(homesData || []);

    // Fetch all homeowners (non-system managers) for dropdown
    const { data: homeownersData, error: homeownersError } = await supabase
      .from('profiles')
      .select('id, full_name')
      .eq('is_system_manager', false)
      .order('full_name');

    if (homeownersError) throw homeownersError;
    setHomeowners(homeownersData || []);

    // Fetch all admin team members (system managers) for assignment dropdown
    const { data: adminData, error: adminError } = await supabase
      .from('profiles')
      .select('id, full_name')
      .eq('is_system_manager', true)
      .order('full_name');

    if (adminError) throw adminError;
    setAdminTeamMembers(adminData || []);
  };

  const searchHomesAndHomeowners = async (query: string) => {
    if (query.length < 2) {
      setHomeSearchResults([]);
      setShowHomeSearchResults(false);
      return;
    }

    setHomeSearchLoading(true);
    try {
      const searchPattern = `%${query}%`;
      
      // Search homes with owner information
      const { data, error } = await supabase
        .from('homes')
        .select(`
          id,
          address,
          city,
          state,
          owner:profiles!homes_owner_id_fkey (
            full_name
          )
        `)
        .or(`address.ilike.${searchPattern},city.ilike.${searchPattern},state.ilike.${searchPattern}`)
        .limit(10);

      if (error) throw error;

      // Also search by homeowner name
      const { data: ownerData, error: ownerError } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          homes:homes!homes_owner_id_fkey (
            id,
            address,
            city,
            state
          )
        `)
        .ilike('full_name', searchPattern)
        .eq('is_system_manager', false)
        .limit(10);

      if (ownerError) throw ownerError;

      // Combine and format results
      const homeResults = (data || []).map((home: any) => {
        const ownerName = home.owner?.full_name || 'Unknown Owner';
        const display = `${home.address}, ${home.city}, ${home.state} - ${ownerName}`;
        return {
          id: home.id,
          address: home.address,
          city: home.city,
          state: home.state,
          owner_name: ownerName,
          display
        };
      });

      const ownerResults = (ownerData || [])
        .flatMap((owner: any) => 
          (owner.homes || []).map((home: any) => {
            const display = `${home.address}, ${home.city}, ${home.state} - ${owner.full_name}`;
            return {
              id: home.id,
              address: home.address,
              city: home.city,
              state: home.state,
              owner_name: owner.full_name,
              display
            };
          })
        );

      // Combine and deduplicate by home id
      const allResults = [...homeResults, ...ownerResults];
      const uniqueResults = Array.from(
        new Map(allResults.map(item => [item.id, item])).values()
      ).slice(0, 10);

      setHomeSearchResults(uniqueResults);
      setShowHomeSearchResults(uniqueResults.length > 0);
    } catch (error: any) {
      console.error('Error searching homes:', error);
      setHomeSearchResults([]);
      setShowHomeSearchResults(false);
    } finally {
      setHomeSearchLoading(false);
    }
  };

  // Debounced search effect for service request form
  useEffect(() => {
    if (homeSearchQuery.length < 2) {
      setHomeSearchResults([]);
      setShowHomeSearchResults(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchHomesAndHomeowners(homeSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [homeSearchQuery]);

  // Debounced search effect for task form
  useEffect(() => {
    if (taskHomeSearchQuery.length < 2) {
      setTaskHomeSearchResults([]);
      setShowTaskHomeSearchResults(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchHomesForTask(taskHomeSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [taskHomeSearchQuery]);

  const searchHomesForTask = async (query: string) => {
    if (query.length < 2) {
      setTaskHomeSearchResults([]);
      setShowTaskHomeSearchResults(false);
      return;
    }

    setTaskHomeSearchLoading(true);
    try {
      const searchPattern = `%${query}%`;
      
      // Search homes with owner information
      const { data, error } = await supabase
        .from('homes')
        .select(`
          id,
          address,
          city,
          state,
          owner:profiles!homes_owner_id_fkey (
            full_name
          )
        `)
        .or(`address.ilike.${searchPattern},city.ilike.${searchPattern},state.ilike.${searchPattern}`)
        .limit(10);

      if (error) throw error;

      // Also search by homeowner name
      const { data: ownerData, error: ownerError } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          homes:homes!homes_owner_id_fkey (
            id,
            address,
            city,
            state
          )
        `)
        .ilike('full_name', searchPattern)
        .eq('is_system_manager', false)
        .limit(10);

      if (ownerError) throw ownerError;

      // Combine and format results
      const homeResults = (data || []).map((home: any) => {
        const ownerName = home.owner?.full_name || 'Unknown Owner';
        const display = `${home.address}, ${home.city}, ${home.state} - ${ownerName}`;
        return {
          id: home.id,
          address: home.address,
          city: home.city,
          state: home.state,
          owner_name: ownerName,
          display
        };
      });

      const ownerResults = (ownerData || [])
        .flatMap((owner: any) => 
          (owner.homes || []).map((home: any) => {
            const display = `${home.address}, ${home.city}, ${home.state} - ${owner.full_name}`;
            return {
              id: home.id,
              address: home.address,
              city: home.city,
              state: home.state,
              owner_name: owner.full_name,
              display
            };
          })
        );

      // Combine and deduplicate by home id
      const allResults = [...homeResults, ...ownerResults];
      const uniqueResults = Array.from(
        new Map(allResults.map(item => [item.id, item])).values()
      ).slice(0, 10);

      setTaskHomeSearchResults(uniqueResults);
      setShowTaskHomeSearchResults(uniqueResults.length > 0);
    } catch (error: any) {
      console.error('Error searching homes for task:', error);
      setTaskHomeSearchResults([]);
      setShowTaskHomeSearchResults(false);
    } finally {
      setTaskHomeSearchLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!currentUser) return;

    setCreatingTask(true);
    try {
      const taskData = {
        title: quickCreateForm.title,
        description: quickCreateForm.description || null,
        priority: quickCreateForm.priority,
        due_date: quickCreateForm.due_date
          ? (parseLocalDate(quickCreateForm.due_date)?.toISOString() || null)
          : null,
        home_id: quickCreateForm.home_id || null,
        homeowner_id: null,
        service_request_id: quickCreateForm.service_request_id || null,
        created_by: currentUser.id,
        assigned_to: quickCreateForm.assigned_to || currentUser.id,
        status: 'pending'
      };

      const { error } = await supabase
        .from('admin_tasks')
        .insert([taskData]);

      if (error) throw error;

      // Reset form
      setQuickCreateForm({
        title: '',
        description: '',
        priority: 'normal',
        due_date: getTodayDate(),
        home_id: '',
        service_request_id: '',
        assigned_to: currentUser.id
      });
      setTaskHomeSearchQuery('');
      setSelectedTaskHome(null);
      setTaskHomeSearchResults([]);
      setShowTaskHomeSearchResults(false);
      setQuickCreateOpen(false);

      // Refresh tasks
      await fetchTasks(currentUser.id);
    } catch (error: any) {
      console.error('Error creating task:', error);
      setError(error.message);
    } finally {
      setCreatingTask(false);
    }
  };

  const handleCreateServiceRequest = async () => {
    if (!currentUser) return;

    setCreatingRequest(true);
    setServiceRequestError(null);
    try {
      const { error } = await supabase
        .from('service_requests')
        .insert([{
          title: quickCreateRequestForm.title,
          description: quickCreateRequestForm.description,
          priority: quickCreateRequestForm.priority,
          home_id: quickCreateRequestForm.home_id || null,
          owner_id: currentUser.id,
          status: 'pending'
        }]);

      if (error) throw error;

      // Reset form and search states
      setQuickCreateRequestForm({
        title: '',
        description: '',
        priority: 'normal',
        home_id: ''
      });
      setHomeSearchQuery('');
      setSelectedHome(null);
      setHomeSearchResults([]);
      setShowHomeSearchResults(false);
      setQuickCreateRequestOpen(false);
      setServiceRequestError(null);

      // Refresh service requests list
      await fetchServiceRequests(currentUser.id);
      await fetchAllOpenServiceRequests();
    } catch (error: any) {
      console.error('Error creating service request:', error);
      setServiceRequestError(error.message);
    } finally {
      setCreatingRequest(false);
    }
  };

  const handleToggleTaskStatus = async (taskId: string, currentStatus: AdminTask['status']) => {
    if (!currentUser) return;

    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const updateData: any = { 
      status: newStatus,
      completed_at: newStatus === 'completed' ? new Date().toISOString() : null
    };

    try {
      const { error } = await supabase
        .from('admin_tasks')
        .update(updateData)
        .eq('id', taskId);

      if (error) throw error;

      // Refresh tasks
      await fetchTasks(currentUser.id);
    } catch (error: any) {
      console.error('Error updating task:', error);
      setError(error.message);
    }
  };

  const handleDeleteTask = (taskId: string) => {
    setTaskToDelete(taskId);
    setShowDeleteConfirmModal(true);
  };

  const toggleTaskExpanded = (taskId: string) => {
    setExpandedTaskCards(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const toggleRequestExpanded = (requestId: string) => {
    setExpandedRequestCards(prev => {
      const next = new Set(prev);
      if (next.has(requestId)) next.delete(requestId);
      else next.add(requestId);
      return next;
    });
  };

  const toggleVisitExpanded = (visitId: string) => {
    setExpandedVisitCards(prev => {
      const next = new Set(prev);
      if (next.has(visitId)) next.delete(visitId);
      else next.add(visitId);
      return next;
    });
  };

  const getVisitStatusColor = (status: UpcomingVisit['status']) => {
    switch (status) {
      case 'in_progress': return { color: '#2563eb', backgroundColor: '#dbeafe' };
      case 'scheduled': return { color: '#ca8a04', backgroundColor: '#fef9c3' };
      case 'proposed': return { color: '#ea580c', backgroundColor: '#ffedd5' };
      case 'requested': return { color: '#7c3aed', backgroundColor: '#ede9fe' };
      case 'completed': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      default: return { color: '#4b5563', backgroundColor: '#f3f4f6' };
    }
  };

  const getVisitTypeColor = (type: UpcomingVisit['visit_type']) => {
    switch (type) {
      case 'maintenance': return { color: '#7c3aed', backgroundColor: '#ede9fe' };
      case 'service': return { color: '#ea580c', backgroundColor: '#ffedd5' };
      case 'walkthrough': return { color: '#2563eb', backgroundColor: '#dbeafe' };
      default: return { color: '#4b5563', backgroundColor: '#f3f4f6' };
    }
  };

  const formatVisitWhen = (scheduledDate: string | null) => {
    if (!scheduledDate) return 'Pending scheduling';
    const date = new Date(scheduledDate);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (sameDay(date, today)) return `Today, ${timeStr}`;
    if (sameDay(date, tomorrow)) return `Tomorrow, ${timeStr}`;

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const isVisitOverdue = (visit: UpcomingVisit) => {
    if (!visit.scheduled_date) return false;
    if (visit.status === 'in_progress') return false;
    return new Date(visit.scheduled_date) < new Date();
  };

  const confirmDeleteTask = async () => {
    if (!currentUser || !taskToDelete) return;

    setDeletingTask(true);
    try {
      const { error } = await supabase
        .from('admin_tasks')
        .delete()
        .eq('id', taskToDelete);

      if (error) throw error;

      // Refresh tasks
      await fetchTasks(currentUser.id);
      
      // Close modal and reset state
      setShowDeleteConfirmModal(false);
      setTaskToDelete(null);
    } catch (error: any) {
      console.error('Error deleting task:', error);
      setError(error.message);
    } finally {
      setDeletingTask(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getPriorityColor = (priority: AdminTask['priority']) => {
    switch (priority) {
      case 'urgent': return { color: '#dc2626', backgroundColor: '#fee2e2' };
      case 'high': return { color: '#ea580c', backgroundColor: '#ffedd5' };
      case 'normal': return { color: '#2563eb', backgroundColor: '#dbeafe' };
      case 'low': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      default: return { color: '#4b5563', backgroundColor: '#f3f4f6' };
    }
  };

  const getStatusColor = (status: ServiceRequest['status']) => {
    switch (status) {
      case 'completed': return { color: '#16a34a', backgroundColor: '#dcfce7' };
      case 'in_progress': return { color: '#2563eb', backgroundColor: '#dbeafe' };
      case 'cancelled': return { color: '#4b5563', backgroundColor: '#f3f4f6' };
      default: return { color: '#ea580c', backgroundColor: '#ffedd5' };
    }
  };

  const isOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const isDueToday = (dueDate: string | null) => {
    if (!dueDate) return false;
    const today = new Date();
    const due = new Date(dueDate);
    return today.toDateString() === due.toDateString();
  };

  // Calculate stats
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfToday);
  endOfWeek.setDate(startOfToday.getDate() + 7);
  const visitsThisWeek = upcomingVisits.filter(v => {
    if (!v.scheduled_date) return false;
    const d = new Date(v.scheduled_date);
    return d >= startOfToday && d < endOfWeek;
  }).length;

  const stats = {
    pendingRequests: serviceRequests.filter(r => r.status === 'pending').length,
    dueToday: tasks.filter(t => isDueToday(t.due_date)).length,
    overdue: tasks.filter(t => isOverdue(t.due_date)).length,
    homesManaged: new Set(serviceRequests.map(r => r.home_id)).size, // Count unique homes
    visitsThisWeek,
  };

  const unreadMessageItems = Array.from(
    new Map(
      serviceRequests
        .map((request) => {
          const homeownerId = (request.home as any)?.owner?.id || request.owner_id;
          const unreadCount = homeownerId ? (unreadMessagesByHomeowner[homeownerId] || 0) : 0;
          if (!homeownerId || unreadCount <= 0) return null;

          return [
            homeownerId,
            {
              homeownerId,
              homeownerName: (request.home as any)?.owner?.full_name || 'Unknown',
              homeAddress: request.home?.address || 'No address',
              unreadCount,
              oldestUnreadAt: oldestUnreadByHomeowner[homeownerId] || null,
            }
          ] as const;
        })
        .filter((item): item is readonly [string, { homeownerId: string; homeownerName: string; homeAddress: string; unreadCount: number; oldestUnreadAt: string | null }] => Boolean(item))
    ).values()
  );

  const formatRelativeTime = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Welcome Header */}
      <View style={styles.welcomeHeader}>
        <Text style={styles.welcomeTitle}>
          {getGreeting()}, {currentUser?.full_name}!
        </Text>
        <Text style={styles.welcomeSubtitle}>Here's what needs your attention today</Text>
      </View>

      {/* Quick Stats */}
      <View style={styles.statsGrid}>
        <View style={styles.statCard}>
          <View style={styles.statContent}>
            <View>
              <Text style={styles.statLabel}>Pending Requests</Text>
              <Text style={[styles.statValue, { color: '#ea580c' }]}>{stats.pendingRequests}</Text>
            </View>
            <MessageSquare size={32} color="#ea580c" style={{ opacity: 0.5 }} />
          </View>
        </View>

        <View style={styles.statCard}>
          <View style={styles.statContent}>
            <View>
              <Text style={styles.statLabel}>Tasks Due Today</Text>
              <Text style={[styles.statValue, { color: '#2563eb' }]}>{stats.dueToday}</Text>
            </View>
            <Calendar size={32} color="#2563eb" style={{ opacity: 0.5 }} />
          </View>
        </View>

        <View style={styles.statCard}>
          <View style={styles.statContent}>
            <View>
              <Text style={styles.statLabel}>Overdue Tasks</Text>
              <Text style={[styles.statValue, { color: '#dc2626' }]}>{stats.overdue}</Text>
            </View>
            <AlertCircle size={32} color="#dc2626" style={{ opacity: 0.5 }} />
          </View>
        </View>

        <View style={styles.statCard}>
          <View style={styles.statContent}>
            <View>
              <Text style={styles.statLabel}>Visits This Week</Text>
              <Text style={[styles.statValue, { color: '#7c3aed' }]}>{stats.visitsThisWeek}</Text>
            </View>
            <Calendar size={32} color="#7c3aed" style={{ opacity: 0.5 }} />
          </View>
        </View>

        <View style={styles.statCard}>
          <View style={styles.statContent}>
            <View>
              <Text style={styles.statLabel}>Active Homes</Text>
              <Text style={[styles.statValue, { color: '#16a34a' }]}>{stats.homesManaged}</Text>
            </View>
            <HomeIcon size={32} color="#16a34a" style={{ opacity: 0.5 }} />
          </View>
        </View>
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {/* Main Content Grid */}
      <View style={styles.mainGrid}>
        {/* Left Side - Main Content (2/3) */}
        <View style={styles.mainContent}>
          {/* Unread Messages Section */}
          <View style={styles.inboxWidget}>
            <Pressable style={styles.inboxWidgetHeader} onPress={() => setMessagesExpanded(prev => !prev)}>
              <View style={styles.sectionTitleRow}>
                {messagesExpanded ? <ChevronDown size={16} color="#6b7280" /> : <ChevronRight size={16} color="#6b7280" />}
                <MessageSquare size={16} color="#9ca3af" />
                <Text style={styles.inboxWidgetTitle}>Unread Messages</Text>
              </View>
              <Text style={styles.inboxWidgetCount}>
                {totalUnreadMessages} {totalUnreadMessages === 1 ? 'message' : 'messages'}
              </Text>
            </Pressable>

            {messagesExpanded && unreadMessageItems.length === 0 ? (
              <View style={styles.emptyState}>
                <MessageSquare size={48} color="#d1d5db" />
                <Text style={styles.emptyText}>No unread messages</Text>
                <Text style={styles.emptySubtext}>Unread homeowner messages will appear here</Text>
              </View>
            ) : messagesExpanded ? (
              <View style={styles.messagesListCompact}>
                {unreadMessageItems.map((item) => (
                  <Pressable
                    key={item.homeownerId}
                    style={styles.compactMessageRow}
                    onPress={() => navigate('/admin?tab=conversations')}
                  >
                    <View style={styles.compactCardLeft}>
                      <View style={[styles.compactStatusBadge, styles.unreadMessagesBadge]}>
                        <Text style={[styles.compactStatusText, styles.unreadMessagesBadgeText]}>
                          {item.unreadCount}
                        </Text>
                      </View>
                      <Text style={styles.compactTitle} numberOfLines={1}>{item.homeownerName}</Text>
                    </View>
                    <View style={styles.compactCardRight}>
                      {item.oldestUnreadAt && (
                        <Text style={styles.compactMessageTime}>
                          {formatRelativeTime(item.oldestUnreadAt)}
                        </Text>
                      )}
                      <View style={styles.compactDetailsButton}>
                        <ExternalLink size={11} color="#ffffff" />
                      </View>
                    </View>
                  </Pressable>
                ))}
              </View>
            ) : null}
          </View>

          {/* Tasks Section */}
          <View style={styles.inboxWidget}>
            <Pressable style={styles.inboxWidgetHeader} onPress={() => setTasksExpanded(prev => !prev)}>
              <View style={styles.sectionTitleRow}>
                {tasksExpanded ? <ChevronDown size={16} color="#6b7280" /> : <ChevronRight size={16} color="#6b7280" />}
                <ClipboardList size={16} color="#9ca3af" />
                <Text style={styles.inboxWidgetTitle}>My Tasks</Text>
              </View>
              <Text style={styles.inboxWidgetCount}>
                {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
              </Text>
            </Pressable>

            {tasksExpanded && tasks.length === 0 ? (
              <View style={styles.emptyState}>
                <ClipboardList size={48} color="#d1d5db" />
                <Text style={styles.emptyText}>No active tasks</Text>
                <Text style={styles.emptySubtext}>Create a task to get started</Text>
              </View>
            ) : tasksExpanded ? (
              <View style={styles.tasksListCompact}>
                {tasks.map((task) => {
                  const priorityColors = getPriorityColor(task.priority);
                  const statusColors = getStatusColor(task.status as ServiceRequest['status']);
                  const overdue = isOverdue(task.due_date);
                  const isExpanded = expandedTaskCards.has(task.id);
                  return (
                    <View
                      key={task.id}
                      style={[
                        styles.compactCard,
                        overdue && styles.taskCardOverdue
                      ]}
                    >
                      <Pressable style={styles.compactCardHeader} onPress={() => toggleTaskExpanded(task.id)}>
                        <View style={styles.compactCardLeft}>
                          {isExpanded ? (
                            <ChevronDown size={14} color="#9ca3af" />
                          ) : (
                            <ChevronRight size={14} color="#9ca3af" />
                          )}
                          <Pressable
                            style={[styles.compactStatusBadge, { backgroundColor: statusColors.backgroundColor }]}
                            onPress={(e) => {
                              e.stopPropagation();
                              handleToggleTaskStatus(task.id, task.status);
                            }}
                          >
                            <Text style={[styles.compactStatusText, { color: statusColors.color }]}>
                              {task.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </Text>
                          </Pressable>
                          <View style={[styles.compactPriorityDot, { backgroundColor: priorityColors.color }]} />
                          <Text style={styles.compactTitle} numberOfLines={1}>{task.title}</Text>
                          {task.homeowner?.full_name && (
                            <Text style={styles.compactOwnerName} numberOfLines={1}>{task.homeowner.full_name}</Text>
                          )}
                        </View>
                        <View style={styles.compactCardRight}>
                          {task.due_date && (
                            <View style={styles.compactDueDateBadge}>
                              <Calendar size={11} color={overdue ? '#dc2626' : '#6b7280'} />
                              <Text style={[styles.compactDueDateText, overdue && styles.compactDueDateOverdue]}>
                                {new Date(task.due_date).toLocaleDateString()}
                              </Text>
                            </View>
                          )}
                        </View>
                      </Pressable>

                      {isExpanded && (
                        <View style={styles.compactCardBody}>
                          {task.description && (
                            <Text style={styles.taskDescription}>{task.description}</Text>
                          )}
                          <View style={styles.taskMetaCompact}>
                            {task.home && (
                              <View style={styles.metaItem}>
                                <HomeIcon size={14} color="#9ca3af" />
                                <Text style={styles.metaText}>
                                  {task.home.address}, {task.home.city}, {task.home.state}
                                </Text>
                              </View>
                            )}
                            {task.service_request_id && task.service_request?.title && (
                              <Pressable
                                style={styles.metaItem}
                                onPress={() => navigate(`/admin/service-request/${task.service_request_id}`)}
                              >
                                <MessageSquare size={14} color="#2563eb" />
                                <Text style={styles.metaLinkText}>{task.service_request.title}</Text>
                              </Pressable>
                            )}
                            {task.due_date && (
                              <View style={styles.metaItem}>
                                <Clock size={14} color="#9ca3af" />
                                <Text style={styles.metaText}>
                                  {isDueToday(task.due_date)
                                    ? 'Due today'
                                    : overdue
                                    ? 'Overdue'
                                    : `Due ${new Date(task.due_date).toLocaleDateString()}`}
                                </Text>
                              </View>
                            )}
                          </View>
                          <View style={styles.compactCardActions}>
                            <View style={[styles.badge, { backgroundColor: priorityColors.backgroundColor }]}>
                              <Text style={[styles.badgeText, { color: priorityColors.color }]}>
                                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                              </Text>
                            </View>
                            {task.service_request_id && (
                              <Pressable
                                style={styles.linkButton}
                                onPress={() => navigate(`/admin/service-request/${task.service_request_id}`)}
                              >
                                <ExternalLink size={12} color="#2563eb" />
                                <Text style={styles.linkButtonText}>View Request</Text>
                              </Pressable>
                            )}
                          </View>
                          <View style={styles.taskExpandedActions}>
                            <Pressable
                              style={styles.taskActionButton}
                              onPress={() => handleToggleTaskStatus(task.id, task.status)}
                            >
                              <View style={[
                                styles.checkboxInner,
                                task.status === 'completed' && styles.checkboxChecked
                              ]}>
                                {task.status === 'completed' && (
                                  <Check size={12} color="#ffffff" />
                                )}
                              </View>
                              <Text style={styles.taskActionButtonText}>
                                {task.status === 'completed' ? 'Completed' : 'Mark Complete'}
                              </Text>
                            </Pressable>
                            <Pressable
                              style={styles.taskActionButtonDanger}
                              onPress={() => handleDeleteTask(task.id)}
                            >
                              <Trash2 size={14} color="#dc2626" />
                              <Text style={styles.taskActionButtonDangerText}>Delete</Text>
                            </Pressable>
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            ) : null}
          </View>

          {/* Upcoming Visits Section */}
          <View style={styles.inboxWidget}>
            <Pressable style={styles.inboxWidgetHeader} onPress={() => setVisitsExpanded(prev => !prev)}>
              <View style={styles.sectionTitleRow}>
                {visitsExpanded ? <ChevronDown size={16} color="#6b7280" /> : <ChevronRight size={16} color="#6b7280" />}
                <Calendar size={16} color="#9ca3af" />
                <Text style={styles.inboxWidgetTitle}>Upcoming Visits</Text>
              </View>
              <Text style={styles.inboxWidgetCount}>
                {upcomingVisits.length} {upcomingVisits.length === 1 ? 'visit' : 'visits'}
              </Text>
            </Pressable>

            {visitsExpanded && upcomingVisits.length === 0 ? (
              <View style={styles.emptyState}>
                <Calendar size={48} color="#d1d5db" />
                <Text style={styles.emptyText}>No upcoming visits</Text>
                <Text style={styles.emptySubtext}>Scheduled visits at your homes will appear here</Text>
              </View>
            ) : visitsExpanded ? (
              <View style={styles.requestsListCompact}>
                {upcomingVisits.map((visit) => {
                  const statusColors = getVisitStatusColor(visit.status);
                  const typeColors = getVisitTypeColor(visit.visit_type);
                  const overdue = isVisitOverdue(visit);
                  const isExpanded = expandedVisitCards.has(visit.id);
                  const visitTitle = visit.internal_name?.trim()
                    || `${visit.visit_type.charAt(0).toUpperCase() + visit.visit_type.slice(1)} visit`;
                  const ownerName = visit.home?.owner?.full_name || 'Unknown';
                  const homeAddress = visit.home
                    ? `${visit.home.address}, ${visit.home.city}, ${visit.home.state}`
                    : 'No home';
                  return (
                    <View
                      key={visit.id}
                      style={[
                        styles.compactCard,
                        overdue && styles.taskCardOverdue,
                      ]}
                    >
                      <Pressable style={styles.compactCardHeader} onPress={() => toggleVisitExpanded(visit.id)}>
                        <View style={styles.compactCardLeft}>
                          {isExpanded ? (
                            <ChevronDown size={14} color="#9ca3af" />
                          ) : (
                            <ChevronRight size={14} color="#9ca3af" />
                          )}
                          <View style={[styles.compactStatusBadge, { backgroundColor: statusColors.backgroundColor }]}>
                            <Text style={[styles.compactStatusText, { color: statusColors.color }]}>
                              {visit.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </Text>
                          </View>
                          <View style={[styles.compactPriorityDot, { backgroundColor: typeColors.color }]} />
                          <Text style={styles.compactTitle} numberOfLines={1}>{visitTitle}</Text>
                          <Text style={styles.compactOwnerName} numberOfLines={1}>{ownerName}</Text>
                        </View>
                        <View style={styles.compactCardRight}>
                          <View style={styles.compactDueDateBadge}>
                            <Clock size={11} color={overdue ? '#dc2626' : '#6b7280'} />
                            <Text style={[styles.compactDueDateText, overdue && styles.compactDueDateOverdue]}>
                              {formatVisitWhen(visit.scheduled_date)}
                            </Text>
                          </View>
                          <Pressable
                            style={styles.compactDetailsButton}
                            onPress={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/visit?id=${visit.id}`);
                            }}
                          >
                            <ExternalLink size={11} color="#ffffff" />
                          </Pressable>
                        </View>
                      </Pressable>

                      {isExpanded && (
                        <View style={styles.compactCardBody}>
                          {visit.visit_notes && (
                            <Text style={styles.requestDescription}>{visit.visit_notes}</Text>
                          )}
                          <View style={styles.taskMetaCompact}>
                            <View style={styles.metaItem}>
                              <HomeIcon size={14} color="#9ca3af" />
                              <Text style={styles.metaText}>{homeAddress}</Text>
                            </View>
                            {visit.scheduled_date && (
                              <View style={styles.metaItem}>
                                <Clock size={14} color={overdue ? '#dc2626' : '#9ca3af'} />
                                <Text style={[styles.metaText, overdue && styles.metaTextOverdue]}>
                                  {new Date(visit.scheduled_date).toLocaleString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  })}
                                  {visit.estimated_duration_minutes
                                    ? ` · ${visit.estimated_duration_minutes} min`
                                    : ''}
                                </Text>
                              </View>
                            )}
                            {visit.service_provider?.company_name && (
                              <View style={styles.metaItem}>
                                <Wrench size={14} color="#9ca3af" />
                                <Text style={styles.metaText}>{visit.service_provider.company_name}</Text>
                              </View>
                            )}
                          </View>
                          <View style={styles.compactCardActions}>
                            <View style={[styles.badge, { backgroundColor: typeColors.backgroundColor }]}>
                              <Text style={[styles.badgeText, { color: typeColors.color }]}>
                                {visit.visit_type.charAt(0).toUpperCase() + visit.visit_type.slice(1)}
                              </Text>
                            </View>
                            <Pressable
                              style={styles.linkButton}
                              onPress={() => navigate(`/admin/visit?id=${visit.id}`)}
                            >
                              <ExternalLink size={12} color="#2563eb" />
                              <Text style={styles.linkButtonText}>Open Visit</Text>
                            </Pressable>
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            ) : null}
          </View>

          {/* Service Requests Section */}
          <View style={styles.inboxWidget}>
            <Pressable style={styles.inboxWidgetHeader} onPress={() => setRequestsExpanded(prev => !prev)}>
              <View style={styles.sectionTitleRow}>
                {requestsExpanded ? <ChevronDown size={16} color="#6b7280" /> : <ChevronRight size={16} color="#6b7280" />}
                <MessageSquare size={16} color="#9ca3af" />
                <Text style={styles.inboxWidgetTitle}>My Service Requests</Text>
              </View>
              <Text style={styles.inboxWidgetCount}>
                {serviceRequests.length} {serviceRequests.length === 1 ? 'request' : 'requests'}
              </Text>
            </Pressable>

            {requestsExpanded && serviceRequests.length === 0 ? (
              <View style={styles.emptyState}>
                <MessageSquare size={48} color="#d1d5db" />
                <Text style={styles.emptyText}>No pending service requests</Text>
              </View>
            ) : requestsExpanded ? (
              <View style={styles.requestsListCompact}>
                {serviceRequests.map((request) => {
                  const priorityColors = getPriorityColor(request.priority);
                  const statusColors = getStatusColor(request.status);
                  const homeownerId = (request.home as any)?.owner?.id || request.owner_id;
                  const unreadCount = homeownerId ? (unreadMessagesByHomeowner[homeownerId] || 0) : 0;
                  const isExpanded = expandedRequestCards.has(request.id);
                  return (
                    <View
                      key={request.id}
                      style={styles.compactCard}
                    >
                      <Pressable style={styles.compactCardHeader} onPress={() => toggleRequestExpanded(request.id)}>
                        <View style={styles.compactCardLeft}>
                          {isExpanded ? (
                            <ChevronDown size={14} color="#9ca3af" />
                          ) : (
                            <ChevronRight size={14} color="#9ca3af" />
                          )}
                          <View style={[styles.compactStatusBadge, { backgroundColor: statusColors.backgroundColor }]}>
                            <Text style={[styles.compactStatusText, { color: statusColors.color }]}>
                              {request.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            </Text>
                          </View>
                          <View style={[styles.compactPriorityDot, { backgroundColor: priorityColors.color }]} />
                          <Text style={styles.compactTitle} numberOfLines={1}>{request.title}</Text>
                          <Text style={styles.compactOwnerName} numberOfLines={1}>
                            {(request.home as any)?.owner?.full_name || 'Unknown'}
                          </Text>
                        </View>
                        <View style={styles.compactCardRight}>
                          {unreadCount > 0 && (
                            <Text style={styles.compactUnreadFlag}>{unreadCount} unread</Text>
                          )}
                          <Pressable
                            style={styles.compactDetailsButton}
                            onPress={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/service-request/${request.id}`);
                            }}
                          >
                            <ExternalLink size={11} color="#ffffff" />
                          </Pressable>
                        </View>
                      </Pressable>

                      {isExpanded && (
                        <View style={styles.compactCardBody}>
                          <Text style={styles.requestDescription}>{request.description}</Text>
                          <View style={styles.taskMetaCompact}>
                            <View style={styles.metaItem}>
                              <HomeIcon size={14} color="#9ca3af" />
                              <Text style={styles.metaText}>
                                {request.home?.address}, {request.home?.city}, {request.home?.state}
                              </Text>
                            </View>
                            <View style={styles.metaItem}>
                              <Clock size={14} color="#9ca3af" />
                              <Text style={styles.metaText}>
                                {new Date(request.created_at).toLocaleDateString()}
                              </Text>
                            </View>
                          </View>
                          <View style={styles.compactCardActions}>
                            <View style={[styles.badge, { backgroundColor: priorityColors.backgroundColor }]}>
                              <Text style={[styles.badgeText, { color: priorityColors.color }]}>
                                {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                              </Text>
                            </View>
                            {unreadCount > 0 && (
                              <View style={[styles.badge, styles.unreadMessagesBadge]}>
                                <Text style={[styles.badgeText, styles.unreadMessagesBadgeText]}>
                                  {unreadCount} unread
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            ) : null}
          </View>
        </View>

        {/* Right Side - Sidebar (1/3) */}
        <View style={styles.sidebar}>
          {/* Quick Create Task */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <View style={styles.sectionTitleRow}>
                <Plus size={20} color="#9ca3af" />
                <Text style={styles.sectionTitle}>Quick Create Task</Text>
              </View>
            </View>

            <View style={styles.form}>
              <TextInput
                style={styles.formInput}
                value={quickCreateForm.title}
                onChangeText={(value) => setQuickCreateForm({ ...quickCreateForm, title: value })}
                placeholder="Task title *"
              />

              {quickCreateOpen && (
                <>
                  <TextInput
                    style={[styles.formInput, styles.textArea]}
                    value={quickCreateForm.description}
                    onChangeText={(value) => setQuickCreateForm({ ...quickCreateForm, description: value })}
                    placeholder="Description (optional)"
                    multiline
                    numberOfLines={2}
                  />

                  <View style={styles.formRow}>
                    <View style={styles.pickerContainer}>
                      <Picker
                        selectedValue={quickCreateForm.priority}
                        onValueChange={(value) => setQuickCreateForm({ ...quickCreateForm, priority: value as AdminTask['priority'] })}
                        style={styles.picker}
                      >
                        <Picker.Item label="Low" value="low" />
                        <Picker.Item label="Normal" value="normal" />
                        <Picker.Item label="High" value="high" />
                        <Picker.Item label="Urgent" value="urgent" />
                      </Picker>
                    </View>

                    {Platform.OS === 'web' ? (
                      <View style={styles.quickCreateDateInputWrap}>
                        <input
                          type="date"
                          value={quickCreateForm.due_date}
                          onChange={(e) =>
                            setQuickCreateForm({ ...quickCreateForm, due_date: e.target.value })
                          }
                          style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            padding: '8px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: 6,
                            fontSize: 14,
                            backgroundColor: '#ffffff',
                          }}
                        />
                      </View>
                    ) : (
                      <TextInput
                        style={styles.formInput}
                        value={quickCreateForm.due_date}
                        onChangeText={(value) => setQuickCreateForm({ ...quickCreateForm, due_date: value })}
                        placeholder="YYYY-MM-DD"
                        placeholderTextColor="#9ca3af"
                      />
                    )}
                  </View>

                  {/* Searchable Home Input */}
                  <View ref={taskHomeSearchRef} style={[
                    styles.typeaheadContainer,
                    Platform.OS === 'web' && { overflow: 'visible' as any, zIndex: showTaskHomeSearchResults ? 10000 : 1000 }
                  ]}>
                    {selectedTaskHome ? (
                      <View style={styles.selectedHome}>
                        <Text style={styles.selectedHomeText} numberOfLines={1}>
                          {selectedTaskHome.display}
                        </Text>
                        <Pressable
                          onPress={() => {
                            setSelectedTaskHome(null);
                            setTaskHomeSearchQuery('');
                            setQuickCreateForm({ ...quickCreateForm, home_id: '' });
                            setShowTaskHomeSearchResults(false);
                          }}
                        >
                          <X size={16} color="#6b7280" />
                        </Pressable>
                      </View>
                    ) : (
                      <>
                        <TextInput
                          style={styles.formInput}
                          value={taskHomeSearchQuery}
                          onChangeText={(value) => {
                            setTaskHomeSearchQuery(value);
                            setShowTaskHomeSearchResults(true);
                          }}
                          onFocus={() => {
                            if (taskHomeSearchResults.length > 0) {
                              setShowTaskHomeSearchResults(true);
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowTaskHomeSearchResults(false), 200);
                          }}
                          placeholder="Search by address, city, or homeowner name... (optional)"
                        />
                        {taskHomeSearchLoading && (
                          <View style={styles.searchLoading}>
                            <ActivityIndicator size="small" color="#9ca3af" />
                          </View>
                        )}
                        {showTaskHomeSearchResults && taskHomeSearchResults.length > 0 && (
                          <View style={styles.typeaheadDropdown}>
                            <WebScrollView style={styles.dropdownScrollView}>
                              {taskHomeSearchResults.map((result) => (
                                <Pressable
                                  key={result.id}
                                  style={styles.typeaheadItem}
                                  onPress={() => {
                                    setSelectedTaskHome({ id: result.id, display: result.display });
                                    setQuickCreateForm({ ...quickCreateForm, home_id: result.id });
                                    setTaskHomeSearchQuery('');
                                    setShowTaskHomeSearchResults(false);
                                  }}
                                >
                                  <Text style={styles.typeaheadItemText}>{result.display}</Text>
                                </Pressable>
                              ))}
                            </WebScrollView>
                          </View>
                        )}
                        {showTaskHomeSearchResults && taskHomeSearchQuery.trim() && taskHomeSearchResults.length === 0 && !taskHomeSearchLoading && (
                          <View style={styles.typeaheadDropdown}>
                            <View style={styles.typeaheadItem}>
                              <Text style={styles.typeaheadItemText}>No homes found</Text>
                            </View>
                          </View>
                        )}
                      </>
                    )}
                  </View>

                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={quickCreateForm.service_request_id}
                      onValueChange={(value) => setQuickCreateForm({ ...quickCreateForm, service_request_id: value })}
                      style={styles.picker}
                    >
                      <Picker.Item label="Associate service request (optional)" value="" />
                      {allOpenServiceRequests.map((request) => {
                        const ownerName = (request.home as any)?.owner?.full_name || 'Unknown owner';
                        const address = request.home?.address || 'No address';
                        return (
                          <Picker.Item
                            key={request.id}
                            label={`${request.title} - ${ownerName} - ${address}`}
                            value={request.id}
                          />
                        );
                      })}
                    </Picker>
                  </View>

                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={quickCreateForm.assigned_to}
                      onValueChange={(value) => setQuickCreateForm({ ...quickCreateForm, assigned_to: value })}
                      style={styles.picker}
                    >
                      {adminTeamMembers.map((member) => (
                        <Picker.Item 
                          key={member.id} 
                          label={member.id === currentUser?.id ? `${member.full_name} (Me)` : member.full_name} 
                          value={member.id} 
                        />
                      ))}
                    </Picker>
                  </View>
                </>
              )}

              <View style={styles.formActions}>
                {!quickCreateOpen && (
                  <Pressable
                    style={styles.secondaryButton}
                    onPress={() => setQuickCreateOpen(true)}
                  >
                    <Text style={styles.secondaryButtonText}>More Options</Text>
                  </Pressable>
                )}
                <Pressable
                  style={[styles.primaryButton, creatingTask && styles.buttonDisabled]}
                  onPress={handleCreateTask}
                  disabled={creatingTask}
                >
                  {creatingTask ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.primaryButtonText}>Create Task</Text>
                  )}
                </Pressable>
              </View>
            </View>
          </View>

          {/* Quick Create Service Request */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <View style={styles.sectionTitleRow}>
                <MessageSquare size={20} color="#9ca3af" />
                <Text style={styles.sectionTitle}>Quick Create Service Request</Text>
              </View>
            </View>

            <View style={styles.form}>
              {serviceRequestError && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{serviceRequestError}</Text>
                </View>
              )}
              <TextInput
                style={styles.formInput}
                value={quickCreateRequestForm.title}
                onChangeText={(value) => {
                  setQuickCreateRequestForm({ ...quickCreateRequestForm, title: value });
                  setServiceRequestError(null);
                }}
                placeholder="Request title *"
              />

              {quickCreateRequestOpen && (
                <>
                  <TextInput
                    style={[styles.formInput, styles.textArea]}
                    value={quickCreateRequestForm.description}
                    onChangeText={(value) => {
                      setQuickCreateRequestForm({ ...quickCreateRequestForm, description: value });
                      setServiceRequestError(null);
                    }}
                    placeholder="Description *"
                    multiline
                    numberOfLines={2}
                  />

                  <View style={styles.pickerContainer}>
                    <Picker
                      selectedValue={quickCreateRequestForm.priority}
                      onValueChange={(value) => {
                        setQuickCreateRequestForm({ ...quickCreateRequestForm, priority: value as 'low' | 'normal' | 'high' | 'urgent' });
                        setServiceRequestError(null);
                      }}
                      style={styles.picker}
                    >
                      <Picker.Item label="Low" value="low" />
                      <Picker.Item label="Normal" value="normal" />
                      <Picker.Item label="High" value="high" />
                      <Picker.Item label="Urgent" value="urgent" />
                    </Picker>
                  </View>

                  {/* Searchable Home Input */}
                  <View ref={homeSearchRef} style={[
                    styles.typeaheadContainer,
                    Platform.OS === 'web' && { overflow: 'visible' as any, zIndex: showHomeSearchResults ? 10000 : 1000 }
                  ]}>
                    {selectedHome ? (
                      <View style={styles.selectedHome}>
                        <Text style={styles.selectedHomeText} numberOfLines={1}>
                          {selectedHome.display}
                        </Text>
                        <Pressable
                          onPress={() => {
                            setSelectedHome(null);
                            setHomeSearchQuery('');
                            setQuickCreateRequestForm({ ...quickCreateRequestForm, home_id: '' });
                            setShowHomeSearchResults(false);
                          }}
                        >
                          <X size={16} color="#6b7280" />
                        </Pressable>
                      </View>
                    ) : (
                      <>
                        <TextInput
                          style={styles.formInput}
                          value={homeSearchQuery}
                          onChangeText={(value) => {
                            setHomeSearchQuery(value);
                            setShowHomeSearchResults(true);
                            setServiceRequestError(null);
                          }}
                          onFocus={() => {
                            if (homeSearchResults.length > 0) {
                              setShowHomeSearchResults(true);
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowHomeSearchResults(false), 200);
                          }}
                          placeholder="Search by address, city, or homeowner name... (optional)"
                        />
                        {homeSearchLoading && (
                          <View style={styles.searchLoading}>
                            <ActivityIndicator size="small" color="#9ca3af" />
                          </View>
                        )}
                        {showHomeSearchResults && homeSearchResults.length > 0 && (
                          <View style={styles.typeaheadDropdown}>
                            <WebScrollView style={styles.dropdownScrollView}>
                              {homeSearchResults.map((result) => (
                                <Pressable
                                  key={result.id}
                                  style={styles.typeaheadItem}
                                  onPress={() => {
                                    setSelectedHome({ id: result.id, display: result.display });
                                    setQuickCreateRequestForm({ ...quickCreateRequestForm, home_id: result.id });
                                    setHomeSearchQuery('');
                                    setShowHomeSearchResults(false);
                                  }}
                                >
                                  <Text style={styles.typeaheadItemText}>{result.display}</Text>
                                </Pressable>
                              ))}
                            </WebScrollView>
                          </View>
                        )}
                        {showHomeSearchResults && homeSearchQuery.trim() && homeSearchResults.length === 0 && !homeSearchLoading && (
                          <View style={styles.typeaheadDropdown}>
                            <View style={styles.typeaheadItem}>
                              <Text style={styles.typeaheadItemText}>No homes found</Text>
                            </View>
                          </View>
                        )}
                      </>
                    )}
                  </View>
                </>
              )}

              <View style={styles.formActions}>
                {!quickCreateRequestOpen && (
                  <Pressable
                    style={styles.secondaryButton}
                    onPress={() => {
                      setQuickCreateRequestOpen(true);
                      setServiceRequestError(null);
                    }}
                  >
                    <Text style={styles.secondaryButtonText}>More Options</Text>
                  </Pressable>
                )}
                <Pressable
                  style={[styles.primaryButton, creatingRequest && styles.buttonDisabled]}
                  onPress={handleCreateServiceRequest}
                  disabled={creatingRequest}
                >
                  {creatingRequest ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <Text style={styles.primaryButtonText}>Create Request</Text>
                  )}
                </Pressable>
              </View>
            </View>
          </View>

          {/* Notepad */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <View style={styles.sectionTitleRow}>
                <StickyNote size={20} color="#9ca3af" />
                <Text style={styles.sectionTitle}>My Notepad</Text>
              </View>
              {notepadSaving && (
                <ActivityIndicator size="small" color="#9ca3af" />
              )}
              {notepadSaved && (
                <View style={styles.savedIndicator}>
                  <CheckCircle2 size={16} color="#16a34a" />
                  <Text style={styles.savedText}>Saved</Text>
                </View>
              )}
            </View>

            <TextInput
              style={[styles.formInput, styles.notepadInput]}
              value={notepad}
              onChangeText={setNotepad}
              placeholder="Jot down quick notes..."
              multiline
              numberOfLines={10}
              textAlignVertical="top"
            />
            <Text style={styles.notepadHint}>Auto-saves after you stop typing</Text>
          </View>
        </View>
      </View>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => !deletingTask && setShowDeleteConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <Pressable 
            style={styles.modalBackdrop} 
            onPress={() => !deletingTask && setShowDeleteConfirmModal(false)} 
          />
          <View style={styles.deleteModalContent}>
            <View style={styles.deleteModalHeader}>
              <View style={styles.deleteModalIconContainer}>
                <Trash2 size={24} color="#dc2626" />
              </View>
              <Text style={styles.deleteModalTitle}>Delete Task</Text>
            </View>
            
            <View style={styles.deleteModalBody}>
              <Text style={styles.deleteModalText}>
                Are you sure you want to delete this task? This action cannot be undone.
              </Text>
            </View>

            <View style={styles.deleteModalActions}>
              <Pressable
                style={styles.deleteModalCancelButton}
                onPress={() => {
                  if (!deletingTask) {
                    setShowDeleteConfirmModal(false);
                    setTaskToDelete(null);
                  }
                }}
                disabled={deletingTask}
              >
                <Text style={styles.deleteModalCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.deleteModalConfirmButton, deletingTask && styles.buttonDisabled]}
                onPress={confirmDeleteTask}
                disabled={deletingTask}
              >
                {deletingTask ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <Text style={styles.deleteModalConfirmText}>Delete</Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 28,
    paddingBottom: 24,
    gap: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 48,
  },
  welcomeHeader: {
    paddingBottom: 4,
  },
  welcomeTitle: {
    fontSize: 26,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  welcomeSubtitle: {
    fontSize: 14,
    color: '#6b7280',
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  statCard: {
    flex: 1,
    minWidth: 150,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 16,
    ...shadows.sm,
  },
  statContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 4,
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
  },
  errorContainer: {
    backgroundColor: '#fee2e2',
    borderWidth: 1,
    borderColor: '#fecaca',
    borderRadius: 8,
    padding: 16,
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  mainGrid: {
    flexDirection: 'row',
    gap: 24,
    flexWrap: 'wrap',
    marginTop: 8,
  },
  mainContent: {
    flex: 2,
    minWidth: 300,
    gap: 24,
  },
  sidebar: {
    flex: 1,
    minWidth: 300,
    gap: 24,
  },
  section: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#f3f4f6',
    padding: 24,
    ...shadows.sm,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000000',
  },
  sectionCount: {
    fontSize: 14,
    color: '#6b7280',
  },
  sectionHeaderCompact: {
    marginBottom: 12,
  },
  sectionCountCompact: {
    fontSize: 12,
  },
  compactInboxSection: {
    padding: 16,
  },
  inboxWidget: {
    gap: 0,
  },
  inboxWidgetHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 2,
    marginBottom: 10,
  },
  inboxWidgetTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  inboxWidgetCount: {
    fontSize: 12,
    color: '#9ca3af',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 12,
  },
  emptySubtext: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 4,
  },
  list: {
    gap: 12,
  },
  compactList: {
    gap: 8,
  },
  tasksListCompact: {
    gap: 0,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  requestsListCompact: {
    gap: 0,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  inboxCard: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  compactCard: {
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  compactCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
  },
  compactCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  compactStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    flexShrink: 0,
  },
  compactStatusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  compactPriorityDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    flexShrink: 0,
  },
  compactCardRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  messagesListCompact: {
    gap: 0,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  compactMessageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  compactMessageTime: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: '500',
  },
  compactUnreadFlag: {
    fontSize: 11,
    color: '#7c3aed',
    fontWeight: '600',
  },
  compactDetailsButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: '#111827',
    justifyContent: 'center',
    alignItems: 'center',
  },
  compactCardBody: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    paddingTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    backgroundColor: '#fafafa',
    gap: 10,
  },
  compactCardActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  taskExpandedActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  taskActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#ffffff',
  },
  taskActionButtonText: {
    fontSize: 13,
    color: '#374151',
    fontWeight: '500',
  },
  taskActionButtonDanger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#fecaca',
    backgroundColor: '#fef2f2',
  },
  taskActionButtonDangerText: {
    fontSize: 13,
    color: '#dc2626',
    fontWeight: '500',
  },
  requestCard: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    padding: 16,
  },
  requestHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  requestTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
    flex: 1,
  },
  requestDescription: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 12,
  },
  requestFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  requestBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  requestMeta: {
    flexDirection: 'row',
    gap: 16,
  },
  compactFooter: {
    alignItems: 'flex-start',
  },
  compactMetaRow: {
    gap: 10,
  },
  unreadMessagesBadge: {
    backgroundColor: '#ede9fe',
  },
  unreadMessagesBadgeText: {
    color: '#7c3aed',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaItemOverdue: {
    // Additional styles for overdue items
  },
  metaText: {
    fontSize: 12,
    color: '#6b7280',
  },
  metaLinkText: {
    fontSize: 12,
    color: '#2563eb',
    textDecorationLine: 'underline',
  },
  metaTextOverdue: {
    color: '#dc2626',
    fontWeight: '500',
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeText: {
    fontSize: 12,
    fontWeight: '500',
  },
  compactBadge: {
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
  compactBadgeText: {
    fontSize: 11,
  },
  taskCard: {
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    padding: 12,
  },
  taskCardOverdue: {
    borderColor: '#fecaca',
    backgroundColor: '#fef2f2',
  },
  taskContent: {
    flexDirection: 'row',
    gap: 12,
  },
  taskContentCompact: {
    gap: 8,
  },
  checkbox: {
    marginTop: 2,
  },
  checkboxInner: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#d1d5db',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxInnerCompact: {
    width: 18,
    height: 18,
  },
  checkboxChecked: {
    backgroundColor: '#16a34a',
    borderColor: '#16a34a',
  },
  taskDetails: {
    flex: 1,
  },
  taskHeader: {
    flex: 1,
  },
  taskTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  taskTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#111827',
    flex: 1,
  },
  compactTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#111827',
    flex: 1,
    minWidth: 0,
  },
  compactOwnerName: {
    fontSize: 12,
    color: '#6b7280',
    flexShrink: 0,
    maxWidth: 140,
  },
  taskActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  deleteButton: {
    padding: 4,
  },
  taskDescription: {
    fontSize: 14,
    color: '#4b5563',
    marginTop: 4,
    marginBottom: 8,
  },
  compactPreview: {
    fontSize: 12,
    marginBottom: 6,
  },
  taskMetaCompact: {
    gap: 6,
  },
  compactDueDateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  compactDueDateText: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: '500',
  },
  compactDueDateOverdue: {
    color: '#dc2626',
    fontWeight: '600',
  },
  taskMeta: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#bfdbfe',
    backgroundColor: '#eff6ff',
  },
  linkButtonText: {
    fontSize: 12,
    color: '#2563eb',
    fontWeight: '500',
  },
  form: {
    gap: 12,
  },
  formRow: {
    flexDirection: 'row',
    gap: 8,
  },
  quickCreateDateInputWrap: {
    flex: 1,
    minWidth: 0,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    backgroundColor: '#ffffff',
  },
  textArea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
  picker: {
    height: Platform.OS === 'ios' ? 200 : 50,
  },
  formActions: {
    flexDirection: 'row',
    gap: 8,
  },
  primaryButton: {
    flex: 1,
    backgroundColor: '#000000',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '500',
  },
  secondaryButton: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  secondaryButtonText: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '500',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  typeaheadContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  selectedHome: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    backgroundColor: '#f9fafb',
  },
  selectedHomeText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
  },
  searchLoading: {
    position: 'absolute',
    right: 12,
    top: 10,
  },
  typeaheadDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    ...shadows.md,
    maxHeight: 300,
    zIndex: 99999,
    elevation: Platform.OS === 'android' ? 10 : undefined,
  },
  dropdownScrollView: {
    maxHeight: 300,
  },
  typeaheadItem: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  typeaheadItemText: {
    fontSize: 14,
    color: '#111827',
  },
  savedIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  savedText: {
    fontSize: 14,
    color: '#16a34a',
  },
  notepadInput: {
    minHeight: 200,
    textAlignVertical: 'top',
  },
  notepadHint: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  deleteModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    ...shadows.lg,
  },
  deleteModalHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  deleteModalIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#fee2e2',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  deleteModalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  deleteModalBody: {
    marginBottom: 24,
  },
  deleteModalText: {
    fontSize: 14,
    color: '#4b5563',
    textAlign: 'center',
    lineHeight: 20,
  },
  deleteModalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  deleteModalCancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  deleteModalCancelText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
  },
  deleteModalConfirmButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
    backgroundColor: '#dc2626',
  },
  deleteModalConfirmText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
});

export default MyDayTab;
